/** Copyright (c) 2018 T. Rowe Price. All rights reserved. */

import './TransactionPendingTable.scss';

import React, { Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { camelCase, isEmpty, kebabCase, map } from 'lodash';

import CancelTransactionButton from 'components/layouts/Transaction/CancelTransaction/CancelTransaction';

import Optioner from 'components/shared/Optioner/Optioner';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';

import {
  Dollar,
  FormattedDate,
  Percent,
  Shares
} from 'components/shared/Text/Text';

import {
  isActivePlus,
  isBrokerage,
  isCollegeSavings,
  isActiveAdvisory,
  MUTUAL_FUND
} from 'services/Products';

import {
  isAddressOfRecordDeliveryType,
  isBaseTransactionType,
  isDistributionToSchoolTransactionType,
  isDTSMailCheckToSchool,
  isElectronicDeliveryType,
  isExchangeTransactionType,
  isPurchaseTransactionType,
  isRedeemTransactionType,
  isWireDeliveryType
} from 'services/Accounts';
import Content from 'services/Content';
import SchoolAddress from './SchoolAddress/SchoolAddress';
import { isMoneyMarketCutoffEnabled } from 'services/FeatureFlags';

const AVAILABLE_SHARES_LABEL = 'All Available Shares';
const REMAINING_SHARES_LABEL = 'All Remaining Shares';

const GO_TUITION = 'Web529Gift';

const DOLLAR_UNIT_TYPE_CODE = 1;
const SHARES_UNIT_TYPE_CODE = 2;
const PERCENT_UNIT_TYPE_CODE = 3;
const AVAILABLE_SHARES_UNIT_TYPE_CODE = 4;
const REMAINING_SHARES_UNIT_TYPE_CODE = 5;

const transactionColumns = [
  'Trade Date',
  'Type',
  'From',
  'To',
  'Cost Basis',
  'Amount',
  'Cancel Button'
];

const collegeSavingsTransactionColumns = [
  'Trade Date',
  'Type',
  'From',
  'To',
  'Amount',
  'Cancel Button'
];

const TransactionPendingHeaders = ({ columns = [] }) => (
  <tr className="columns">
    {columns.map((name) => {
      const colName = name !== 'Cancel Button' ? name : '';
      return (
        <th
          scope="col"
          className={`transactions-pending-header-cell test-${camelCase(
            name
          )} transactions-pending-${kebabCase(name)}`}
          key={name}>
          {colName}
        </th>
      );
    })}
  </tr>
);

const TradeDate = ({ tradeDate = '' }) => (
  <FormattedDate
    element="td"
    className="transactions-pending-cell transactions-pending-trade-date">
    {tradeDate}
  </FormattedDate>
);

const Type = ({ typeInformation = {} }) => {
  const { confirmationNumber, typeDescriptor } = typeInformation;

  if (typeDescriptor && confirmationNumber) {
    return (
      <td className="transactions-pending-cell transactions-pending-type">
        <div className="transactions-pending-type-descriptor">
          {typeDescriptor === 'DistributionToSchool'
            ? 'Distribution'
            : typeDescriptor}
        </div>
        <div className="transactions-pending-type-confirmation">
          {confirmationNumber}
        </div>
      </td>
    );
  }

  return <DefaultCell />;
};

const BankAccountNumber = ({ bankAccountNumber }) => (
  <Fragment>{`Bank Account No. ${bankAccountNumber}`}</Fragment>
);

const AccountNumberOwners = ({ accountWChecksum, owners }) => (
  <div>
    {accountWChecksum}
    {!isEmpty(owners) ? <span className="spacing-dot" /> : ''}
    {!isEmpty(owners) ? owners : ''}
  </div>
);

const AccountNumberOwnersWithPermissions =
  WithUserPermissions(AccountNumberOwners);

const BankAccountNumberWithPermissions = WithUserPermissions(BankAccountNumber);

const FromAccount = ({
  accountName,
  accountType,
  accountWChecksum,
  owners,
  ticker,
  isViewOnly
}) => (
  <Fragment>
    <span className="transactions-pending-account-type">
      {accountType} - {accountName}
    </span>
    &nbsp;
    <span className="transactions-pending-account-ticker">{ticker}</span>
    {isViewOnly && <span className="tag-label">View Only</span>}
    <AccountNumberOwnersWithPermissions
      accountWChecksum={accountWChecksum}
      owners={owners}
      shouldMask
    />
  </Fragment>
);

const FromAccountInfo = ({ fromInformation = {}, bankAccountNumber = '' }) => {
  const { accountFromInfo, engagementName, type } = fromInformation;

  const isGiftTransactions =
    isPurchaseTransactionType(type) && engagementName === GO_TUITION;
  const isAlternateTransactions =
    isPurchaseTransactionType(type) ||
    ((isRedeemTransactionType(type) ||
      isExchangeTransactionType(type) ||
      isBaseTransactionType(type)) &&
      engagementName === GO_TUITION);
  const hasAccountInfo = !!accountFromInfo;

  return (
    <td className="transactions-pending-cell transactions-pending-from">
      <Optioner
        val={{ isGiftTransactions, isAlternateTransactions, hasAccountInfo }}>
        {{
          isGiftTransactions: 'GoTuition',
          isAlternateTransactions: () =>
            isEmpty(bankAccountNumber) ? (
              '-'
            ) : (
              <BankAccountNumberWithPermissions
                bankAccountNumber={bankAccountNumber}
                shouldMask
              />
            ),
          hasAccountInfo: () => <FromAccount {...accountFromInfo} />,
          _: '-'
        }}
      </Optioner>
    </td>
  );
};

const ToAccountInfo = ({ toInformation = {}, bankAccountNumber = '' }) => {
  const {
    accountToInfo,
    deliveryType,
    toName,
    toTicker,
    toAccountWChecksum,
    type
  } = toInformation;

  if (isRedeemTransactionType(type)) {
    if (isWireDeliveryType(deliveryType)) {
      return (
        <td className="transactions-pending-cell transactions-pending-to">
          <span className="transactions-pending-masked-account-number">
            Wire to your Bank of Record
          </span>
          <div>
            {isEmpty(bankAccountNumber) ? (
              '-'
            ) : (
              <BankAccountNumberWithPermissions
                bankAccountNumber={bankAccountNumber}
                shouldMask
              />
            )}
          </div>
        </td>
      );
    }

    if (isElectronicDeliveryType(deliveryType)) {
      return (
        <td className="transactions-pending-cell transactions-pending-to">
          <span className="transactions-pending-masked-account-number">
            Electronic Transfer to your Bank of Record
          </span>
          <div>
            {isEmpty(bankAccountNumber) ? (
              '-'
            ) : (
              <BankAccountNumberWithPermissions
                bankAccountNumber={bankAccountNumber}
                shouldMask
              />
            )}
          </div>
        </td>
      );
    }

    if (isAddressOfRecordDeliveryType(deliveryType)) {
      return (
        <td className="transactions-pending-cell transactions-pending-to">
          <span className="transactions-pending-masked-account-number">
            Check to your address of Record
          </span>
        </td>
      );
    }
  }

  if (isDistributionToSchoolTransactionType(type)) {
    if (isDTSMailCheckToSchool(deliveryType)) {
      return (
        <td className="transactions-pending-cell transactions-pending-to">
          <span className="transactions-pending-masked-account-number">
            {toInformation.payeeDetail ? (
              <SchoolAddress
                name={toInformation.payeeDetail?.college?.name}
                address={toInformation.payeeDetail?.payeeAddress}
              />
            ) : (
              'Check to your address of Record'
            )}
          </span>
        </td>
      );
    }
  }

  if (accountToInfo) {
    const {
      accountName,
      accountType,
      accountWChecksum,
      owners,
      ticker,
      productType,
      isViewOnly
    } = accountToInfo;

    if (isActiveAdvisory(productType)) {
      const rasAccountType = 'Retirement Advisory Service - ' + accountType;
      return (
        <td className="transactions-pending-cell transactions-pending-to">
          <span className="transactions-pending-account-type">
            {rasAccountType}
          </span>
          <AccountNumberOwnersWithPermissions
            accountWChecksum={accountWChecksum}
            shouldMask
          />
        </td>
      );
    }

    return (
      <td className="transactions-pending-cell transactions-pending-to">
        <span className="transactions-pending-account-type">
          {accountType} - {accountName}
        </span>
        &nbsp;
        <span className="transactions-pending-account-ticker">{ticker}</span>
        {isViewOnly && <span className="tag-label">View Only</span>}
        <AccountNumberOwnersWithPermissions
          accountWChecksum={accountWChecksum}
          owners={owners}
          shouldMask
        />
      </td>
    );
  }

  if (!isEmpty(toName)) {
    return (
      <td className="transactions-pending-cell transactions-pending-to">
        <span className="transactions-pending-account-type">{toName}</span>
        &nbsp;
        <span className="transactions-pending-account-ticker">{toTicker}</span>
        <AccountNumberOwnersWithPermissions
          accountWChecksum={toAccountWChecksum}
          shouldMask
        />
      </td>
    );
  }

  return <DefaultCell />;
};

const CostBasis = ({ costBasis = null }) => {
  return (
    <td className="transactions-pending-cell transactions-pending-cost-basis">
      {costBasis ? <div>{costBasis}</div> : <div>-</div>}
    </td>
  );
};

const Amount = ({ amountInformation = {} }) => {
  const { amount, quantity, unitCode, productType } = amountInformation;

  return (
    <Optioner val={unitCode}>
      {{
        [DOLLAR_UNIT_TYPE_CODE]: (
          <Dollar
            element="td"
            className="transactions-pending-cell transactions-pending-amount">
            {amount}
          </Dollar>
        ),
        [SHARES_UNIT_TYPE_CODE]: (
          <Shares
            element="td"
            extraDecimal={isBrokerage(productType) || isActivePlus(productType)}
            className="transactions-pending-cell transactions-pending-amount">
            {quantity}
          </Shares>
        ),
        [PERCENT_UNIT_TYPE_CODE]: (
          <Percent
            element="td"
            className="transactions-pending-cell transactions-pending-amount">
            {amount}
          </Percent>
        ),
        [AVAILABLE_SHARES_UNIT_TYPE_CODE]: (
          <td className="transactions-pending-cell transactions-pending-amount">
            {AVAILABLE_SHARES_LABEL}
          </td>
        ),
        [REMAINING_SHARES_UNIT_TYPE_CODE]: (
          <td className="transactions-pending-cell transactions-pending-amount">
            {REMAINING_SHARES_LABEL}
          </td>
        ),
        _: DefaultCell
      }}
    </Optioner>
  );
};

const DefaultCell = () => <td className="transactions-pending-cell">-</td>;

const TransactionPendingCells = ({ rowData, columns }) =>
  columns.map((colName, key) => {
    const col = colName === '' ? 'name' : camelCase(colName);
    return cellComponentMapper(col, {
      ...rowData,
      key
    });
  });

const TransactionPendingRows = ({ rows = [], columns, parentKey = '' }) =>
  map(rows, (row, childKey) => (
    <tr className="transactions-pending-row" key={`${parentKey}:${childKey}`}>
      <TransactionPendingCells rowData={row} columns={columns} />
    </tr>
  ));

const cellComponentMapper = (columnName, props) => {
  const {
    typeInformation,
    fromInformation: { accountFromInfo },
    toInformation: { accountToInfo }
  } = props;
  const mapper = {
    tradeDate: <TradeDate {...props} />,
    type: <Type {...props} />,
    from: <FromAccountInfo {...props} />,
    to: <ToAccountInfo {...props} />,
    costBasis: <CostBasis {...props} />,
    amount: <Amount {...props} />,
    cancelButton: (
      <CancelTransactionButton
        {...props}
        transactionType={props?.fromInformation?.type}
        confirmationNumber={typeInformation.confirmationNumber}
        fromProductType={accountFromInfo && accountFromInfo.productType}
        toProductType={accountToInfo && accountToInfo.productType}
      />
    )
  };
  return mapper[columnName] || <DefaultCell />;
};

const TransactionPendingTable = ({
  rows,
  hasCollegeSavingsOnly,
  productType,
  viewMoreLink,
  includePendingTransactions
}) => {
  const transactionPendingColumns =
    isCollegeSavings(productType) || hasCollegeSavingsOnly
      ? collegeSavingsTransactionColumns
      : transactionColumns;
  return (
    <div className="transactions-pending-table-container module module-container">
      {includePendingTransactions && (
        <div className="account-details-pending-transactions">
          <h2 className="account-details-graph-header flex-header">
            Pending Transactions
            <a className="view-more-link" onClick={viewMoreLink}>
              View Pending History
            </a>
          </h2>
        </div>
      )}

      {isEmpty(rows) ? (
        <div className="transactions-pending-no-transactions">
          You currently do not have any Pending Transactions.
        </div>
      ) : (
        <Fragment>
          <table className="transactions-pending-table">
            <thead className="transactions-pending-header">
              <TransactionPendingHeaders columns={transactionPendingColumns} />
            </thead>
            <tbody>
              <TransactionPendingRows
                rows={rows}
                columns={transactionPendingColumns}
              />
            </tbody>
          </table>
          <p className="transactions-pending-disclosure">
            {isMoneyMarketCutoffEnabled() ? Content.cancelMoneyMarketCutOffDisclosure : Content.cancelPendingTransactionsDisclosure}
          </p>
        </Fragment>
      )}
    </div>
  );
};

TransactionPendingTable.defaultProps = {
  hasCollegeSavingsOnly: false,
  includePendingTransactions: false,
  productType: MUTUAL_FUND,
  viewMoreLink: ''
};

export default withRouter(TransactionPendingTable);
